import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import LoadingSpinner from '../../../Loading/component';
import Sidebar from '../../../Navigation/Sidebar/Sidebar';
import styles from './WarehouseAssortmentDetailsComponent.module.css';

// Icons
import filedownload from "../../../../assests/filedownload.svg";
import calandericon from "../../../../assests/calandericon.svg";
import dragdropimage from "../../../../assests/dragdropimage.svg";
import deleteIconAssortment from "../../../../assests/deleteIconAssortment.svg";
import threeDotIcon from "../../../../assests/threeDotIcon.svg";
import menuFilter from "../../../../assests/menuiFilter.svg";
import sloticon from "../../../../assests/sloticon.svg";
import slotdoubleicon from "../../../../assests/slotdoubleicon.svg";

const WarehouseAssortmentDetailsComponent = ({
  assortmentDetails,
  loading,
  activeTabIndex,
  onTabChange,
  hasChanges,
  onSaveChanges,
  onCancelChanges,
  handleEndAssortment,
  setShowDeleteModal,
  showDeleteModal,
  categories,
  selectedCategory,
  onCategoryChange,
  currentItems,
  remainingItems,
  onItemDrop,
  itemsContainerRef,
  autoScrollDirection,
  onDragUpdate,
  handleCheckRule,
  assortmentSlotDetails = [],
}) => {
  const navigate = useNavigate();
  const { assortmentId } = useParams();
  const [showMenu, setShowMenu] = useState(false);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [userRole, setUserRole] = useState('');
  const menuRef = useRef();
  const dragItemRef = useRef(null);
  const tabsContainerRef = useRef(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("data"));
    if (userData) {
      setUserRole(userData.details.userRole.role_name);
    }
  }, []);

  const isReadOnlyUser = ['manager', 'city lead'].includes(userRole?.toLowerCase());
  const canDownload = ['admin', 'est', 'manager', 'city lead'].includes(userRole?.toLowerCase());

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getAllTabs = () => {
    const tabs = [
      {
        type: 'primary',
        title: 'Primary',
        count: assortmentDetails.priority_wise_items?.length || 0,
        index: 0
      }
    ];

    if (assortmentDetails.hardware_type_items?.length > 0) {
      assortmentDetails.hardware_type_items.forEach((item, index) => {
        tabs.push({
          type: 'hardware',
          title: item.hardware_type,
          count: item.priority_wise_items?.length || 0,
          index: index + 1
        });
      });
    }

    return tabs;
  };

  const tabs = getAllTabs();
  const itemsPerSlide = 4;
  const totalSlides = Math.ceil(tabs.length / itemsPerSlide);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const getVisibleTabs = () => {
    const startIndex = currentSlide * itemsPerSlide;
    const endIndex = startIndex + itemsPerSlide;
    return tabs.slice(startIndex, endIndex);
  };

  const handleDragStart = (e, item, sourceType) => {
    if (isReadOnlyUser) {
      e.preventDefault();
      return;
    }
    setDraggedItem(item);
    setDragSource(sourceType);
    dragItemRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.mv_id);
    e.currentTarget.classList.add(styles.dragging);
  };

  const handleDragOver = (e, index) => {
    if (isReadOnlyUser) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const targetElement = e.currentTarget;
    const boundingRect = targetElement.getBoundingClientRect();
    const offsetY = e.clientY - boundingRect.top;
    const middleThreshold = boundingRect.height / 2;

    targetElement.classList.remove(styles.dragOverTop, styles.dragOverBottom);

    if (offsetY < middleThreshold) {
      targetElement.classList.add(styles.dragOverTop);
    } else {
      targetElement.classList.add(styles.dragOverBottom);
    }

    if (onDragUpdate) {
      onDragUpdate(e);
    }
  };

  const handleDragLeave = (e) => {
    if (isReadOnlyUser) return;
    e.preventDefault();
    e.currentTarget.classList.remove(styles.dragOverTop, styles.dragOverBottom);
  };

  const handleDrop = (e, targetIndex) => {
    if (isReadOnlyUser) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.currentTarget.classList.remove(styles.dragOverTop, styles.dragOverBottom);

    if (draggedItem && dragSource) {
      onItemDrop(draggedItem, targetIndex, dragSource);
    }

    setDraggedItem(null);
    setDragSource(null);
  };

  const handleDragEnd = (e) => {
    if (isReadOnlyUser) return;
    e.currentTarget.classList.remove(styles.dragging);
    setDraggedItem(null);
    setDragSource(null);
  };

  const handleEditAssortment = () => {
    if (isReadOnlyUser) return;
    navigate(`/home/assortment/assortmenteditdetails/${assortmentId}`);
  };

  const handleCloneAssortment = () => {
    if (isReadOnlyUser) return;
    navigate(`/home/assortment/clone/${assortmentId}`);
  };

  const handleDownload = () => {
    if (!canDownload) return;
    const generateCSVContent = () => {
      const {
        id = '',
        reference_id = '',
        reference_name = '',
        start_date = '',
        priority_wise_items = [],
        rule_failures = [],
        hardware_type_items = [],
        created_by_name = '',
        status = '',
        created_date = '',
        updated_by_name = '',
        last_updated_date = ''
      } = assortmentDetails;

      const headers = [
        'Assortment ID', 'Warehouse Name (Warehouse ID)', 'Assortment Status',
        'Start Date', 'VMs Linked', 'Product Rank', 'SKU ID', 'Product Name',
        'Brand Name', 'MRP', 'Variant', 'Item Category', 'Rule Details',
        'Created By', 'Created Date', 'Last Updated By', 'Last Updated Date'
      ];

      const mainData = [
        id, reference_id ? `${reference_name} ${reference_id}` : '',
        status, formatDate(start_date), hardware_type_items.length,
        '', '', '', '', '', '', '', '', created_by_name,
        formatDate(created_date), updated_by_name, formatDate(last_updated_date)
      ].map(value => `"${value}"`).join(',');

      const productRows = priority_wise_items.map((item, index) => {
        const hasRuleFailure = rule_failures.some(rule =>
          rule.failedItems?.ids?.includes(item.mv_id)
        );

        return [
          '', '', '', '', '', index + 1, item.mv_id || '',
          item.variant_name || '', item.brand_name || '',
          item.mrp || item.offer_price || '', item.variant_name || '',
          item.categories?.join(', ') || '', hasRuleFailure ? 'No' : 'Yes',
          '', '', '', ''
        ].map(value => `"${value}"`).join(',');
      });

      return [headers.join(','), mainData, ...productRows].join('\r\n');
    };

    const csvContent = generateCSVContent();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Warehouse_Assortment_${assortmentDetails.id || 'details'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEndAssortmentWithCheck = () => {
    if (isReadOnlyUser) return;
    handleEndAssortment();
  };

  const handleCheckRuleWithCheck = () => {
    if (isReadOnlyUser) return;
    handleCheckRule();
  };

  const handleCategoryChangeWithCheck = (category) => {
    if (isReadOnlyUser) return;
    onCategoryChange(category);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderSlotIcon = (rank) => {
    if (activeTabIndex === 0 || !Array.isArray(assortmentSlotDetails)) return null;

    const matchedSlot = assortmentSlotDetails.find(slot => {
      const expectedRank = slot.slotId % 100;
      return expectedRank === rank;
    });

    if (!matchedSlot) return null;

    return (
      <div className={styles.slotIcon}>
        <img
          src={matchedSlot.isMerged ? slotdoubleicon : sloticon}
          alt={matchedSlot.isMerged ? "Merged Slot" : "Single Slot"}
          style={{ width: "24px", height: "24px" }}
        />
      </div>
    );
  };

  const visibleTabs = getVisibleTabs();

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className={styles.container}>
          <Sidebar />
          <div className={styles.contentWrapper}>
            <div className={styles.headerContainer}>
              <div className={styles.headerLeftSection}>
                <button className={styles.backButton} onClick={() => navigate(-1)}>
                  ←
                </button>
                <div className={styles.titleSection}>
                  <h1 className={styles.title}>
                    Assortment Id: <strong>{assortmentDetails.id || 'N/A'}</strong>
                  </h1>
                  <p className={styles.createdBy}>
                    Created By: {assortmentDetails.created_by_name} {assortmentDetails.created_by || 'N/A'}
                  </p>
                </div>
              </div>
              <div className={styles.headerActions}>
                {!isReadOnlyUser && assortmentDetails.status === "active" && (
                  <>
                    <button onClick={handleCloneAssortment} className={styles.cloneButton}>
                      Clone
                    </button>
                    {canDownload && (
                      <button className={styles.downloadButton} onClick={handleDownload}>
                        <img src={filedownload} alt="Download" />
                      </button>
                    )}
                  </>
                )}

                {!isReadOnlyUser && assortmentDetails.status === "scheduled" && (
                  <>
                    {activeTabIndex === 0 ? (
                      <>
                        <button className={styles.filterButton}>
                          <img
                            src={menuFilter}
                            alt="Filter"
                            onClick={() => setShowFilterPopup(true)}
                            title="Filter by category"
                          />
                        </button>
                        <button className={styles.cloneButton} onClick={handleEditAssortment}>
                          Edit
                        </button>
                        <Dialog
                          open={showFilterPopup}
                          onClose={() => setShowFilterPopup(false)}
                          className="filter-dialog"
                          PaperProps={{ className: "filter-popup-paper" }}
                        >
                          <DialogTitle className="filter-popup-title">Select Category</DialogTitle>
                          <RadioGroup
                            value={selectedCategory}
                            onChange={(e) => {
                              handleCategoryChangeWithCheck(e.target.value);
                              setShowFilterPopup(false);
                            }}
                          >
                            {categories.map((category) => (
                              <FormControlLabel
                                key={category}
                                value={category}
                                control={<Radio />}
                                label={category}
                                className="filter-radio-label"
                              />
                            ))}
                          </RadioGroup>
                        </Dialog>
                        <div className={styles.menuWrapper} ref={menuRef}>
                          {canDownload && (
                            <button
                              className={styles.downloadButton}
                              onClick={() => setShowMenu(!showMenu)}
                              title="More options"
                            >
                              <img src={threeDotIcon} alt="More" />
                            </button>
                          )}
                          {showMenu && (
                            <div className={styles.menuPopup}>
                              <div className={styles.menuItem} onClick={handleDownload}>
                                <img src={filedownload} alt="Download" className={styles.menuIcon} />
                                <span>Download</span>
                              </div>
                              <div className={styles.menuItem} onClick={() => setShowDeleteModal(true)}>
                                <img src={deleteIconAssortment} alt="Delete" className={styles.menuIcon} />
                                <span>Delete</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      canDownload && (
                        <button className={styles.downloadButton} onClick={handleDownload}>
                          <img src={filedownload} alt="Download" />
                        </button>
                      )
                    )}
                  </>
                )}

                {!isReadOnlyUser && assortmentDetails.status === "inactive" && (
                  <>
                    <button className={styles.cloneButton} onClick={handleCloneAssortment}>
                      Clone & Schedule
                    </button>
                    {canDownload && (
                      <button className={styles.downloadButton} onClick={handleDownload}>
                        <img src={filedownload} alt="Download" />
                      </button>
                    )}
                  </>
                )}

                {isReadOnlyUser && canDownload && (
                  <>
                    <button className={styles.downloadButton} onClick={handleDownload}>
                      <img src={filedownload} alt="Download" />
                    </button>
                    <div className={styles.readOnlyBadge}>View Only</div>
                  </>
                )}

                {!canDownload && (
                  <div className={styles.readOnlyBadge}>View Only</div>
                )}
              </div>
            </div>

            {!isReadOnlyUser && (
              <Dialog
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                className="custom-delete-dialog"
              >
                <DialogTitle className="custom-delete-dialog-title">
                  Are you sure you want to Delete This Assortment Id: <strong>{assortmentDetails.id}</strong>?
                </DialogTitle>
                <DialogActions className="custom-delete-dialog-actions">
                  <Button onClick={() => setShowDeleteModal(false)} variant="outlined">No</Button>
                  <Button onClick={handleEndAssortmentWithCheck} variant="contained" color="error">Yes</Button>
                </DialogActions>
              </Dialog>
            )}

            <div className={styles.mainContent}>
              <div className={styles.leftSection}>
                <div className={styles.infoSection}>
                  <div className={styles.dateStatus}>
                    <img src={calandericon} alt="Calendar" className={styles.calendarIcon} />
                    <div className={styles.detailsDateInfo}>
                      <div className={styles.dateRow}>
                        <div className={styles.dateItem}>
                          <span className={styles.dateLabel}>Start Date:</span>
                          <span className={styles.dateValue}>
                            {formatDate(assortmentDetails.start_date)}
                          </span>
                        </div>
                        {assortmentDetails.status === "inactive" && (
                          <div className={styles.dateItem}>
                            <span className={styles.dateLabel}>End Date:</span>
                            <span className={styles.dateValue}>
                              {formatDate(assortmentDetails.end_date)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <span className={`${styles.status} ${styles[assortmentDetails.status?.toLowerCase()]}`}>
                      {assortmentDetails.status}
                    </span>
                  </div>

                  <div className={styles.tabsAndRulesWrapper}>
                    {(assortmentDetails.hardware_type_items?.length > 0 || assortmentDetails.priority_wise_items?.length > 0) && (
                      <div className={styles.hardwareTabsContainer}>
                        <div className={styles.hardwareTabsWrapper}>
                          {totalSlides > 1 && (
                            <button
                              className={`${styles.carouselButton} ${styles.carouselPrev}`}
                              onClick={prevSlide}
                              disabled={currentSlide === 0}
                            >
                              <ChevronLeft />
                            </button>
                          )}

                          <div className={styles.hardwareTabs} ref={tabsContainerRef}>
                            {visibleTabs.map((tab) => (
                              <div
                                key={tab.index}
                                className={`${styles.hardwareTab} ${activeTabIndex === tab.index ? styles.active : ""}`}
                                onClick={() => onTabChange(tab.index)}
                              >
                                <div className={styles.tabTitle}>{tab.title}</div>
                                <div className={styles.tabCount}>{tab.count} Items</div>
                              </div>
                            ))}
                          </div>

                          {totalSlides > 1 && (
                            <button
                              className={`${styles.carouselButton} ${styles.carouselNext}`}
                              onClick={nextSlide}
                              disabled={currentSlide === totalSlides - 1}
                            >
                              <ChevronRight />
                            </button>
                          )}
                        </div>

                        {totalSlides > 1 && (
                          <div className={styles.carouselIndicators}>
                            {Array.from({ length: totalSlides }).map((_, index) => (
                              <button
                                key={index}
                                className={`${styles.carouselIndicator} ${currentSlide === index ? styles.active : ''}`}
                                onClick={() => setCurrentSlide(index)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {assortmentDetails.rule_failures?.length > 0 && (
                      <div className={styles.ruleFailures}>
                        <div className={styles.ruleFailuresContent}>
                          <h2 className={styles.ruleFailuresTitle}>
                            {assortmentDetails.rule_failures.length} Rule Fail
                          </h2>
                          <span>{assortmentDetails.rule_failures[0]?.failureType}</span>
                        </div>
                        {!isReadOnlyUser && (
                          <button className={styles.checkAgainButton} onClick={handleCheckRuleWithCheck}>
                            Check Again
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.itemsWrapper}>
                  <div
                    className={`${styles.itemsContainer} ${autoScrollDirection ? styles[`autoScroll${autoScrollDirection.charAt(0).toUpperCase() + autoScrollDirection.slice(1)}`] : ''} ${isReadOnlyUser ? styles.readOnly : ''}`}
                    ref={itemsContainerRef}
                  >
                    <div className={styles.primarySection}>
                      <div className={styles.sectionHeader}>
                        <h3>
                          {activeTabIndex === 0 ? 'Primary Assortment' : `${tabs[activeTabIndex]?.title} Assortment`}
                          ({currentItems.length} items)
                        </h3>
                      </div>
                      <div className={styles.itemsGrid}>
                        {currentItems.map((item, index) => (
                          <div
                            key={`primary-${item.mv_id}-${index}`}
                            className={`${styles.itemCard} ${item.hasRankChanged ? styles.rankChanged : ''} ${isReadOnlyUser ? styles.readOnlyItem : ''}`}
                            draggable={!isReadOnlyUser}
                            onDragStart={(e) => handleDragStart(e, item, 'primary')}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                            <div className={styles.itemIndex}>
                              {item.rank < 10 ? `0${item.rank}` : item.rank}
                            </div>

                            {renderSlotIcon(item.rank)}

                            <div className={styles.itemContentWrapper}>
                              <div className={styles.itemContent}>
                                <div className={styles.itemImage}>
                                  {item.image ? (
                                    <img
                                      src={`https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/${item.image}`}
                                      alt={item.variant_name}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = "none";
                                        e.target.parentNode.innerHTML = "<span>No Image</span>";
                                      }}
                                    />
                                  ) : (
                                    <span>No Image</span>
                                  )}
                                </div>
                                <div className={styles.itemDetails}>
                                  <div className={styles.itemName}>{item.variant_name}</div>
                                  <div className={styles.itemMeta}>
                                    <span className={styles.itemMvid}>MVID: {item.mv_id}</span>
                                    <span className={styles.itemPrice}>
                                      MRP: ₹{item.mrp || item.offer_price || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {item.hasRankChanged && item.originalRank && (
                                <div className={styles.rankChange}>
                                  <span className={styles.rankChangeText}>
                                    Moved from rank {item.originalRank} ➜ {item.rank}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {remainingItems.length > 0 && (
                      <div className={styles.secondarySection}>
                        <div className={styles.sectionHeader}>
                          <h3>Secondary Assortment ({remainingItems.length} items)</h3>
                          {!isReadOnlyUser && <p>Drag items to add them to the primary assortment</p>}
                        </div>
                        <div className={`${styles.itemsGrid} ${styles.secondaryItemsGrid}`}>
                          {remainingItems.map((item, index) => (
                            <div
                              key={`secondary-${item.mv_id}-${index}`}
                              className={`${styles.itemCard} ${styles.secondaryItem} ${item.hasRankChanged ? styles.rankChanged : ''} ${isReadOnlyUser ? styles.readOnlyItem : ''}`}
                              draggable={!isReadOnlyUser}
                              onDragStart={(e) => handleDragStart(e, item, 'secondary')}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, index)}
                              onDragEnd={handleDragEnd}
                            >
                              <div className={styles.itemIndex}>
                                {item.rank < 10 ? `0${item.rank}` : item.rank}
                              </div>

                              <div className={styles.itemContentWrapper}>
                                <div className={styles.itemContent}>
                                  <div className={styles.itemImage}>
                                    {item.image ? (
                                      <img
                                        src={`https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/${item.image}`}
                                        alt={item.variant_name}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.style.display = "none";
                                          e.target.parentNode.innerHTML = "<span>No Image</span>";
                                        }}
                                      />
                                    ) : (
                                      <span>No Image</span>
                                    )}
                                  </div>
                                  <div className={styles.itemDetails}>
                                    <div className={styles.itemName}>{item.variant_name}</div>
                                    <div className={styles.itemMeta}>
                                      <span className={styles.itemMvid}>MVID: {item.mv_id}</span>
                                      <span className={styles.itemPrice}>
                                        MRP: ₹{item.mrp || item.offer_price || 'N/A'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {item.hasRankChanged && item.originalRank && (
                                  <div className={styles.rankChange}>
                                    <span className={styles.rankChangeText}>
                                      Moved from rank {item.originalRank} ➜ {item.rank}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {!isReadOnlyUser && hasChanges && (
                  <div className={styles.saveChangesBar}>
                    <div className={styles.saveMessage}>
                      <img src={dragdropimage} alt="Drag and drop" className={styles.dragIcon} />
                      Note: To change items priority you can drag & drop items card from the list.
                    </div>
                    <div className={styles.saveActions}>
                      <button className={styles.cancelButton} onClick={onCancelChanges}>Cancel</button>
                      <button className={styles.saveButton} onClick={onSaveChanges}>Save</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WarehouseAssortmentDetailsComponent;